// Pestaña Obra de la Sala de Control: la épica 005 de Calypso como la llevaría un jefe de proyecto (Pau 18-10: eligió
// esta maqueta, web/qa/obra/maqueta-pm.html). De arriba abajo: ¿cómo vamos? (semáforo) → ¿dónde estamos? (hoja de
// ruta) → ¿qué me toca? → avance, tablero, frentes, riesgos y lo último. Todo sale del tablero de Calypso
// (backend/src/sala/obra.ts) y lo que se deduce está en obraResumen.ts: aquí solo se pinta. Horas en hora de Tailandia.
import type { ReactNode } from 'react';
import { t, type TKey } from '../../../i18n';
import { cn } from '../../../lib/utils';
import { Aviso } from '../ui';
import { edad } from './formato';
import { avance, etapasDe, frentes, parada, piezasDe, porcentaje, riesgos, salud, ultimos, ZONA_PAU } from './obraResumen';
import type { ObraSala, PiezaTodaObra } from './tipos';
import { Calculando, ListaPlegable, SinDatos } from './piezas';
import { calculandoPrimeraVez, useSalaRincon } from './useSalaRincon';

/** «hoy, 23:58 de Tailandia» / «16-10, 02:15 de Tailandia». */
export function cuandoPau(iso: string, ahora = new Date()): string {
  const d = new Date(iso);
  const dia = (x: Date) => x.toLocaleDateString('es-ES', { day: '2-digit', month: '2-digit', timeZone: ZONA_PAU }).replace('/', '-');
  const hora = d.toLocaleTimeString('es-ES', { hour: '2-digit', minute: '2-digit', timeZone: ZONA_PAU });
  return t('salaControl.obra.cuando', { dia: dia(d) === dia(ahora) ? t('salaControl.obra.hoy') : dia(d), hora });
}

/** «18-10» de un día AAAA-MM-DD. */
const diaCorto = (dia: string) => `${dia.slice(8, 10)}-${dia.slice(5, 7)}`;

function Caja({ titulo, sub, tono, children }: { titulo: string; sub?: string; tono?: 'oro' | 'aviso'; children: ReactNode }) {
  return (
    <section className={cn('rounded-o-m px-4 py-3', tono === 'oro' ? 'border border-oficina-marca-oscura bg-oficina-burbuja-pau' : 'bg-oficina-elevada')}>
      <div className="mb-2 flex flex-wrap items-baseline justify-between gap-x-3">
        <h4 className={cn('text-o-nota font-bold', tono === 'oro' ? 'text-oficina-acento' : tono === 'aviso' ? 'text-oficina-bloqueado' : 'text-oficina-texto3')}>
          {titulo}
        </h4>
        {sub ? <span className="text-o-nota text-oficina-texto3">{sub}</span> : null}
      </div>
      {children}
    </section>
  );
}

function Semaforo({ d, ahora }: { d: ObraSala; ahora: number }) {
  const s = salud(d, ahora);
  const p = porcentaje(d);
  const detalle =
    s.color === 'rojo'
      ? s.diasQuieta === null
        ? t('salaControl.obra.saludDetalle.rojoNunca')
        : t('salaControl.obra.saludDetalle.rojo', { dias: s.diasQuieta })
      : s.color === 'ambar'
        ? t(s.paradas === 1 ? 'salaControl.obra.saludDetalle.ambarUno' : 'salaControl.obra.saludDetalle.ambar', { n: s.paradas })
        : t('salaControl.obra.saludDetalle.verde');
  return (
    <section
      className={cn(
        'flex items-center gap-4 rounded-o-m border px-4 py-3.5',
        { verde: 'border-oficina-trabajando/40 bg-oficina-elevada', ambar: 'border-oficina-marca-oscura bg-oficina-burbuja-pau', rojo: 'border-oficina-error/60 bg-oficina-elevada' }[s.color],
      )}
    >
      <span
        aria-hidden
        className={cn('h-10 w-10 shrink-0 rounded-full', { verde: 'bg-oficina-trabajando', ambar: 'bg-oficina-acento', rojo: 'bg-oficina-error' }[s.color])}
      />
      <div className="min-w-0 flex-1">
        <p className={cn('text-o-titulo3 font-semibold', { verde: 'text-oficina-trabajando', ambar: 'text-oficina-acento', rojo: 'text-oficina-error-claro' }[s.color])}>
          {t(`salaControl.obra.salud.${s.color}`)}
        </p>
        <p className="text-o-texto text-oficina-texto2">
          {detalle}
          {s.cerradas24h > 0 ? ` ${t(s.cerradas24h === 1 ? 'salaControl.obra.cerradas24hUno' : 'salaControl.obra.cerradas24h', { n: s.cerradas24h })}` : ''}
        </p>
      </div>
      <div className="text-right">
        <p className="text-o-titulo font-bold leading-none">{p.pct} %</p>
        <p className="text-o-nota text-oficina-texto2">{t('salaControl.obra.pctEpica')}</p>
        <p className="text-o-nota text-oficina-texto3">{t('salaControl.obra.piezasDe', { hechas: p.hechas, total: p.total })}</p>
      </div>
    </section>
  );
}

function Barra({ hechas, enCurso, total, alto = 'h-1.5' }: { hechas: number; enCurso: number; total: number; alto?: string }) {
  const pct = (n: number) => `${total ? (100 * n) / total : 0}%`;
  return (
    <div className={cn('flex overflow-hidden rounded-full bg-oficina-linea', alto)}>
      <span className="bg-oficina-trabajando" style={{ width: pct(hechas) }} />
      <span className="bg-oficina-acento" style={{ width: pct(enCurso) }} />
    </div>
  );
}

function HojaDeRuta({ d }: { d: ObraSala }) {
  return (
    <Caja titulo={t('salaControl.obra.ruta')} sub={t('salaControl.obra.rutaSub')}>
      <ol className="grid grid-cols-1 gap-1.5 sm:grid-cols-[2fr_1fr_1fr_1fr_1fr]">
        {etapasDe(d).map((e) => {
          const on = e.numero === d.etapa.numero;
          return (
            <li key={e.numero} className={cn('rounded-o-s px-2.5 py-2', on ? 'bg-oficina-burbuja-pau ring-1 ring-oficina-acento' : 'bg-oficina-panel')}>
              <p className={cn('text-o-mini font-bold', on ? 'text-oficina-acento' : 'text-oficina-texto3')}>
                {t('salaControl.obra.etapaN', { n: e.numero })}
                {on ? ` · ${t('salaControl.obra.estamosAqui')}` : ''}
              </p>
              <p className={cn('text-o-texto font-semibold', on ? 'text-oficina-texto' : 'text-oficina-texto2')}>{e.nombre}</p>
              {e.para ? <p className="text-o-nota leading-snug text-oficina-texto3">{e.para}</p> : null}
              <div className="mt-1.5">
                <Barra hechas={e.hechas} enCurso={e.en_curso} total={e.total} />
              </div>
              {e.total ? <p className="mt-1 text-o-mini text-oficina-texto3">{t('salaControl.obra.etapaCuenta', { hechas: e.hechas, total: e.total })}</p> : null}
            </li>
          );
        })}
      </ol>
    </Caja>
  );
}

function Avance({ d, ahora }: { d: ObraSala; ahora: number }) {
  const a = avance(d, ahora);
  const W = 420;
  const H = 120;
  const x = (i: number) => 28 + (i * (W - 40)) / Math.max(1, a.dias.length - 1);
  const y = (n: number) => H - 22 - (n * (H - 40)) / Math.max(1, a.total);
  const puntos = a.dias.map((p, i) => `${x(i)},${y(p.hechas)}`).join(' ');
  const ultimo = a.dias.at(-1);
  return (
    <Caja titulo={t('salaControl.obra.avance', { n: d.etapa.numero })} sub={t('salaControl.obra.avanceSub')}>
      <svg viewBox={`0 0 ${W} ${H}`} className="h-32 w-full" role="img" aria-label={t('salaControl.obra.avance', { n: d.etapa.numero })}>
        <line x1={28} x2={W - 12} y1={y(a.total)} y2={y(a.total)} className="stroke-oficina-linea" strokeDasharray="3 3" />
        <text x={W - 12} y={y(a.total) - 4} textAnchor="end" className="fill-oficina-texto3 text-o-mini">
          {t('salaControl.obra.meta', { n: a.total })}
        </text>
        <line x1={28} x2={W - 12} y1={y(0)} y2={y(0)} className="stroke-oficina-linea" />
        <polyline points={puntos} fill="none" className="stroke-oficina-trabajando" strokeWidth={2.5} />
        {ultimo ? (
          <text x={x(a.dias.length - 1) - 6} y={y(ultimo.hechas) - 6} textAnchor="end" className="fill-oficina-trabajando text-o-nota font-bold">
            {ultimo.hechas}
          </text>
        ) : null}
        {a.dias.map((p, i) =>
          i === 0 || i === a.dias.length - 1 || a.dias.length <= 8 ? (
            <text key={p.dia} x={x(i)} y={H - 6} textAnchor="middle" className="fill-oficina-texto3 text-o-mini">
              {i === a.dias.length - 1 ? t('salaControl.obra.hoy') : diaCorto(p.dia)}
            </text>
          ) : null,
        )}
      </svg>
      <p className="text-o-sub text-oficina-texto2">
        {a.prevista ? t('salaControl.obra.prevista', { fecha: diaCorto(a.prevista) }) : t('salaControl.obra.sinPrevista', { n: 5 })}
      </p>
    </Caja>
  );
}

function Tarjeta({ p }: { p: PiezaTodaObra }) {
  const q = p.quien.find((x) => x.vivo && x.tema);
  return (
    <li
      className={cn(
        'rounded-o-s border-l-[3px] bg-oficina-panel px-2.5 py-1.5 text-o-sub leading-snug',
        p.estado === 'hecho' ? 'border-oficina-trabajando text-oficina-texto2' : p.estado === 'en curso' ? 'border-oficina-acento' : 'border-oficina-linea',
      )}
    >
      {p.nombre}
      {p.estado === 'hecho' && p.hecho_utc ? <span className="block text-o-mini text-oficina-texto3">{cuandoPau(p.hecho_utc)}</span> : null}
      {parada(p) ? <span className="block text-o-mini text-oficina-bloqueado">{t('salaControl.obra.parada')}</span> : null}
      {q?.tema ? <span className="block text-o-mini text-oficina-texto3">{t('salaControl.obra.lleva', { tema: q.tema })}</span> : null}
    </li>
  );
}

/** La lista plegable de la Sala con los textos de Obra. */
function Plegable<T>(p: { items: T[]; primeras?: number; pinta: (x: T) => ReactNode; clave: (x: T) => string }) {
  return <ListaPlegable {...p} yMas={(n) => t('salaControl.obra.yMas', { n })} verMenos={t('salaControl.obra.verMenos')} />;
}

function TableroEtapa({ d }: { d: ObraSala }) {
  const piezas = piezasDe(d).filter((p) => p.etapa === d.etapa.numero);
  const hechas = piezas.filter((p) => p.estado === 'hecho').sort((a, b) => Date.parse(b.hecho_utc ?? '') - Date.parse(a.hecho_utc ?? ''));
  const cols: Array<{ clave: 'porHacer' | 'enCurso' | 'hecho'; tono: string; items: PiezaTodaObra[] }> = [
    { clave: 'porHacer', tono: 'text-oficina-texto2', items: piezas.filter((p) => p.estado === 'pendiente') },
    { clave: 'enCurso', tono: 'text-oficina-acento', items: piezas.filter((p) => p.estado === 'en curso') },
    { clave: 'hecho', tono: 'text-oficina-trabajando', items: hechas },
  ];
  return (
    <Caja titulo={t('salaControl.obra.tablero', { n: d.etapa.numero })}>
      <div className="grid grid-cols-1 gap-2 sm:grid-cols-3">
        {cols.map((c) => (
          <div key={c.clave}>
            <p className="mb-1.5 flex justify-between text-o-sub font-semibold text-oficina-texto2">
              {t(`salaControl.obra.${c.clave}` as TKey)}
              <span className={c.tono}>{c.items.length}</span>
            </p>
            <Plegable items={c.items} clave={(p) => p.id} pinta={(p) => <Tarjeta p={p} />} />
          </div>
        ))}
      </div>
    </Caja>
  );
}

function TeToca({ d }: { d: ObraSala }) {
  const preguntas = d.preguntas_pendientes ?? [];
  return (
    <Caja titulo={t('salaControl.obra.toca')} sub={t('salaControl.obra.tocaSub')} tono="oro">
      <p className={cn('text-o-texto font-semibold', d.esperando_allow.length ? 'text-oficina-te-necesita' : 'text-oficina-trabajando')}>
        {d.esperando_allow.length ? t('salaControl.obra.allowN', { n: d.esperando_allow.length }) : t('salaControl.obra.allowNada')}
      </p>
      {d.esperando_allow.length ? (
        <ul className="mb-1 mt-1 flex flex-col gap-1 text-o-sub">
          {d.esperando_allow.map((s) => (
            <li key={`${s.displayTitle}-${s.createdAt ?? ''}`}>
              {s.displayTitle}
              {s.createdAt ? <span className="ml-2 text-oficina-texto3">{cuandoPau(s.createdAt)}</span> : null}
            </li>
          ))}
        </ul>
      ) : null}
      {d.preguntas_pendientes ? (
        <>
          <p className={cn('mt-2 text-o-texto font-semibold', preguntas.length ? 'text-oficina-te-necesita' : 'text-oficina-trabajando')}>
            {preguntas.length ? t('salaControl.obra.preguntas', { n: preguntas.length }) : t('salaControl.obra.preguntasNada')}
          </p>
          {preguntas.length ? (
            <div className="mt-1 text-o-sub text-oficina-meta-pau">
              <Plegable items={preguntas} clave={(q) => String(q.numero)} pinta={(q) => <li>{q.texto}</li>} />
            </div>
          ) : null}
        </>
      ) : null}
    </Caja>
  );
}

function Frentes({ d }: { d: ObraSala }) {
  const fs = frentes(d);
  const vivos = fs.filter((f) => f.hechas + f.enCurso > 0);
  const quietos = fs.length - vivos.length;
  return (
    <Caja titulo={t('salaControl.obra.frentes')} sub={t('salaControl.obra.frentesSub')}>
      <ul className="flex flex-col gap-1.5">
        {vivos.map((f) => (
          <li key={f.id} className="grid grid-cols-[1fr_5rem_2.5rem] items-center gap-2 text-o-sub">
            <span className="truncate">{f.nombre}</span>
            <Barra hechas={f.hechas} enCurso={f.enCurso} total={f.total} />
            <span className="text-right text-o-nota text-oficina-texto3">
              {f.hechas}/{f.total}
            </span>
          </li>
        ))}
      </ul>
      {quietos ? <p className="mt-1.5 text-o-nota text-oficina-texto3">{t('salaControl.obra.frentesQuietos', { n: quietos })}</p> : null}
    </Caja>
  );
}

function Riesgos({ d, ahora }: { d: ObraSala; ahora: number }) {
  const rs = riesgos(d, ahora);
  return (
    <Caja titulo={t('salaControl.obra.riesgos')} sub={t('salaControl.obra.riesgosSub')} tono={rs.length ? 'aviso' : undefined}>
      {rs.length ? (
        <ul className="flex list-disc flex-col gap-1 pl-4 text-o-sub">
          {rs.map((r, i) => (
            <li key={r.clave} className={i === 0 ? 'text-oficina-bloqueado' : undefined}>
              {/* 03-11: «1 piezas / 1 subidas» → singular cuando es una. */}
              {t(`salaControl.obra.riesgo.${'n' in r && r.n === 1 && (r.clave === 'paradas' || r.clave === 'allow') ? `${r.clave}Uno` : r.clave}` as TKey, r as unknown as Record<string, string | number>)}
            </li>
          ))}
        </ul>
      ) : (
        <p className="text-o-sub text-oficina-trabajando">{t('salaControl.obra.sinRiesgos')}</p>
      )}
    </Caja>
  );
}

function LoUltimo({ d }: { d: ObraSala }) {
  const ev = ultimos(d);
  if (!ev.length) return null;
  return (
    <Caja titulo={t('salaControl.obra.ultimo')}>
      <ul className="flex flex-col text-o-sub">
        {ev.map((e) => (
          <li key={`${e.tipo}-${e.cuando}-${e.texto}`} className="grid grid-cols-[auto_1fr] gap-x-3 border-b border-oficina-hover py-1 last:border-0">
            <span className="whitespace-nowrap text-o-nota text-oficina-texto3">{cuandoPau(e.cuando)}</span>
            <span>
              <span className={e.tipo === 'hecha' ? 'text-oficina-trabajando' : 'text-oficina-texto2'}>
                {e.tipo === 'hecha' ? t('salaControl.obra.hecha') : t('salaControl.obra.subida')}
              </span>
              {' · '}
              {e.texto}
            </span>
          </li>
        ))}
      </ul>
    </Caja>
  );
}

export function ObraVista({ d, ahora = Date.now() }: { d: ObraSala; ahora?: number }) {
  return (
    <div className="grid grid-cols-1 gap-3 lg:grid-cols-[1.5fr_1fr]">
      <div className="flex min-w-0 flex-col gap-3">
        <Semaforo d={d} ahora={ahora} />
        <HojaDeRuta d={d} />
        <Avance d={d} ahora={ahora} />
        <TableroEtapa d={d} />
      </div>
      <div className="flex min-w-0 flex-col gap-3">
        <TeToca d={d} />
        <Frentes d={d} />
        <Riesgos d={d} ahora={ahora} />
        <LoUltimo d={d} />
      </div>
    </div>
  );
}

export function Obra() {
  const { foto, fallo } = useSalaRincon<ObraSala>('obra');
  if (fallo && !foto) return <Aviso tono="error">{t('salaControl.errorCarga')}</Aviso>;
  if (calculandoPrimeraVez(foto)) return <Calculando />;
  if (!foto) return null;
  const motivo = foto.error ? t(`salaControl.obra.errores.${foto.error.mensaje}` as TKey) : null;
  if (!foto.datos) {
    return motivo ? <Aviso tono="error">{t('salaControl.rincones.sinDatosPorError', { motivo })}</Aviso> : <SinDatos texto={t('salaControl.obra.sinDatos')} />;
  }
  const desfasada = foto.vieja || !!foto.error;
  return (
    <div className="flex flex-col gap-3">
      <div className="flex flex-wrap items-baseline justify-between gap-x-3">
        <h3 className="text-o-titulo3 font-semibold">{t('salaControl.obra.titulo')}</h3>
        {foto.edad_s !== null ? <p className="text-o-nota text-oficina-texto3">{t('salaControl.obra.sub', { edad: edad(foto.edad_s) })}</p> : null}
      </div>
      {desfasada ? (
        <Aviso tono="aviso">
          {t('salaControl.rincones.desfasado', { edad: edad(foto.edad_s ?? 0), motivo: motivo ?? t('salaControl.obra.errores.error') })}
        </Aviso>
      ) : null}
      <div className={cn(desfasada && 'opacity-60')}>
        <ObraVista d={foto.datos} />
      </div>
    </div>
  );
}
